import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Loader2, Save, RotateCcw, Package, ShoppingCart } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { marketplaceService } from '../../services/marketplaceService';
import { CustomerPreviewModal } from './CustomerPreviewModal';
import type { ProductDraft } from '../ProductsTab/ProductDialog';
import type { InclusionItem } from '../inclusionItem';
import { attributesToFeatureStrings } from '../productFeatureSpecs';

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

export type ReorderDomain =
  | 'computers'
  | 'networking'
  | 'mobile-hardware'
  | 'unified-collaboration'
  | 'telstra'
  | 'software'
  | 'consulting';

/**
 * Everything the card grid AND the customer-preview modal need. Field set
 * deliberately mirrors ProductDraft so the preview modal can take the
 * product object as-is without remapping.
 */
export interface ReorderProduct {
  id: string;
  name: string;
  vendorOrBrand: string;
  /** 'hardware' | 'software' | 'consulting' — used to bucket the save payload */
  kind: 'hardware' | 'software' | 'consulting';
  /** Lowercased domain — drives grouping into tabs */
  domain: ReorderDomain;
  priceDisplay?: string;
  /** Numeric price + pricing type used by the customer-preview modal */
  price?: number;
  pricingType?: 'perUser' | 'lumpSum';
  billingCycle?: 'monthly' | 'annual';
  image?: string;
  enabled: boolean;
  displayOrder?: number;
  description?: string;
  features?: string[] | InclusionItem[];
  planInclusions?: string[] | InclusionItem[];
  enrichmentAttributes?: Record<string, unknown>;
  source?: 'dickerdata' | 'ingram' | 'telstra-distributor' | 'manual';
  sku?: string;
  vendorPartNumber?: string;
  soh?: number;
  vendorPreset?: string;
  inclusionApps?: Array<{ name: string; image?: string }>;
}

interface CatalogueReorderViewProps {
  snapshotId: string;
  catalogueName: string;
  products: ReorderProduct[];
  /** Refresh hook called after a successful save. Parent should reload products. */
  onSaved?: () => void;
  /** Wraps a stored asset path (`pack:foo` etc.) into a renderable URL. */
  getCachedImageUrl?: (url?: string) => string | undefined;
  /** True when the parent catalogue is the master "All Products" list. */
  isAllProducts?: boolean;
}

// ────────────────────────────────────────────────────────────────────────────
// Domain metadata
// ────────────────────────────────────────────────────────────────────────────

const DOMAIN_LABELS: Record<ReorderDomain, string> = {
  computers: 'Computers',
  networking: 'Networking',
  'mobile-hardware': 'Telstra Handsets',
  'unified-collaboration': 'Unified Collaboration',
  telstra: 'Telstra Plans',
  software: 'Software',
  consulting: 'Consulting',
};

const DOMAIN_ORDER: ReorderDomain[] = [
  'mobile-hardware',
  'telstra',
  'computers',
  'networking',
  'unified-collaboration',
  'software',
  'consulting',
];

/**
 * Sort by displayOrder ascending, nulls last; tie-break alphabetically by
 * name. Mirrors the storefront sort so the partner sees what customers see.
 */
function sortByDisplayOrder(items: ReorderProduct[]): ReorderProduct[] {
  return [...items].sort((a, b) => {
    const ao = typeof a.displayOrder === 'number' ? a.displayOrder : Number.POSITIVE_INFINITY;
    const bo = typeof b.displayOrder === 'number' ? b.displayOrder : Number.POSITIVE_INFINITY;
    if (ao !== bo) return ao - bo;
    return a.name.localeCompare(b.name);
  });
}

/** Coerce inclusion lists (string[] | InclusionItem[]) to plain string[] for the card preview. */
function inclusionsToTextRows(raw: ReorderProduct['features'] | ReorderProduct['planInclusions']): string[] {
  if (!Array.isArray(raw)) return [];
  return (raw as Array<any>)
    .map((it) => (typeof it === 'string' ? it : (it?.text ?? '')))
    .map((t: string) => t.trim())
    .filter(Boolean);
}

/**
 * Pick which feature/inclusion bullets to show on the card preview. Mirrors
 * the customer-storefront logic in CustomerProductView so the card and the
 * detail modal stay consistent.
 */
function previewBullets(p: ReorderProduct): string[] {
  const isDistributorHardware =
    (p.source === 'dickerdata' || p.source === 'ingram' || p.source === 'telstra-distributor') &&
    (p.domain === 'computers' || p.domain === 'unified-collaboration' || p.domain === 'mobile-hardware');
  if (isDistributorHardware && p.enrichmentAttributes) {
    return attributesToFeatureStrings(p.enrichmentAttributes).slice(0, 4);
  }
  const isSoftware = p.domain === 'software' || p.kind === 'software';
  if (isSoftware) {
    const inclusions = inclusionsToTextRows(p.planInclusions);
    if (inclusions.length > 0) return inclusions.slice(0, 4);
  }
  return inclusionsToTextRows(p.features).slice(0, 4);
}

function priceLine(p: ReorderProduct): string {
  if (typeof p.price !== 'number' || p.price <= 0) {
    return p.priceDisplay || '—';
  }
  const money = `$${p.price.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (p.pricingType === 'perUser') {
    return `${money} / user / ${p.billingCycle === 'annual' ? 'year' : 'month'}`;
  }
  return money;
}

const PLACEHOLDER_IMAGE =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><rect fill="%23f1f5f9" width="80" height="80" rx="8"/><text fill="%2394a3b8" font-family="Arial" font-size="11" x="50%" y="50%" text-anchor="middle" dominant-baseline="middle">No Image</text></svg>';

// ────────────────────────────────────────────────────────────────────────────
// Sortable card — mirrors the customer storefront card shape
// ────────────────────────────────────────────────────────────────────────────

function SortableProductCard({
  product,
  index,
  imageUrl,
  onPreview,
}: {
  product: ReorderProduct;
  index: number;
  imageUrl?: string;
  onPreview: (p: ReorderProduct) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: product.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  const bullets = useMemo(() => previewBullets(product), [product]);
  const price = priceLine(product);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative bg-white rounded-lg border ${isDragging ? 'border-orange-400 shadow-xl' : 'border-slate-200 hover:border-slate-300 hover:shadow-md'} overflow-hidden transition flex flex-col h-full`}
    >
      {/* Drag handle, top-left, only visible on hover/focus to keep card surface clean */}
      <button
        type="button"
        aria-label={`Drag to reorder ${product.name}`}
        className="absolute top-2 left-2 z-10 inline-flex items-center justify-center h-7 w-7 rounded-md bg-white/90 backdrop-blur-sm border border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-300 cursor-grab active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-orange-500 opacity-0 group-hover:opacity-100 focus:opacity-100 shadow-sm"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Position badge, top-left below the drag handle on hover, otherwise sits in the top-left */}
      <div className="absolute top-2 left-2 z-0 inline-flex items-center justify-center h-7 px-2 rounded-md bg-slate-900/80 text-white text-[11px] font-mono tabular-nums group-hover:opacity-0 transition-opacity">
        #{index + 1}
      </div>

      {/* Disabled badge, top-right */}
      {!product.enabled && (
        <div className="absolute top-2 right-2 z-10 inline-flex items-center px-2 py-0.5 rounded-md bg-slate-700/80 text-white text-[10px] font-medium">
          Disabled
        </div>
      )}

      {/* Card body — clickable surface for preview */}
      <button
        type="button"
        onClick={() => onPreview(product)}
        className="flex flex-col flex-1 text-left focus:outline-none focus:ring-2 focus:ring-orange-500 rounded-lg"
      >
        {/* Image — fixed height, contained, mirrors customer storefront 160px tall */}
        <div className="h-40 bg-white border-b border-slate-100 flex items-center justify-center p-4 flex-shrink-0">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={product.name}
              className="max-h-full max-w-full object-contain"
              onError={(e) => { (e.currentTarget as HTMLImageElement).src = PLACEHOLDER_IMAGE; }}
            />
          ) : (
            <Package className="h-12 w-12 text-slate-300" />
          )}
        </div>

        <div className="p-5 flex flex-col flex-1">
          {/* Vendor */}
          <div className="text-xs text-slate-500 mb-1">{product.vendorOrBrand}</div>
          {/* Name */}
          <h3 className="text-base font-semibold text-slate-900 leading-tight mb-3 line-clamp-2">
            {product.name}
          </h3>

          {/* Feature bullets — green checkmarks like the customer card */}
          {bullets.length > 0 && (
            <ul className="space-y-1.5 mb-4 m-0 list-none p-0">
              {bullets.map((b, i) => (
                <li key={i} className="text-xs text-slate-600 inline-flex items-start gap-1.5 w-full">
                  <span className="inline-block h-3.5 w-3.5 mt-0.5 text-emerald-500 flex-shrink-0">
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                  </span>
                  <span className="line-clamp-1">{b}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Price footer — pinned to bottom via mt-auto + flex-1 above */}
          <div className="flex items-center justify-between pt-3 border-t border-slate-100 mt-auto gap-2">
            <div className="text-sm font-semibold text-slate-900 truncate">{price}</div>
            <span
              className="inline-flex items-center gap-1 rounded-md bg-blue-600 text-white text-xs font-medium px-2.5 py-1 opacity-90"
              title="Storefront cosmetic — partners don't add to cart from here"
            >
              <ShoppingCart className="h-3 w-3" />
              Add
            </span>
          </div>
        </div>
      </button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────

/**
 * Drag-and-drop catalogue reorder view styled as a customer-storefront
 * preview. Cards render 3-per-row (1 mobile / 2 tablet / 3 desktop)
 * matching the customer-side layout, with drag handles for reordering
 * and click-through to a read-only customer preview modal.
 *
 * Order persists via marketplaceService.saveCatalogueOrder, which sends
 * only the (id, displayOrder) delta. Sticky save footer appears on dirty.
 * Cancel reverts without round-tripping.
 */
export function CatalogueReorderView({
  snapshotId,
  catalogueName,
  products,
  onSaved,
  getCachedImageUrl,
  isAllProducts: _isAllProducts,
}: CatalogueReorderViewProps) {
  const grouped = useMemo(() => {
    const buckets = new Map<ReorderDomain, ReorderProduct[]>();
    for (const p of products) {
      const list = buckets.get(p.domain) || [];
      list.push(p);
      buckets.set(p.domain, list);
    }
    const out = new Map<ReorderDomain, ReorderProduct[]>();
    buckets.forEach((list, domain) => {
      out.set(domain, sortByDisplayOrder(list));
    });
    return out;
  }, [products]);

  const domainsWithItems = useMemo(
    () => DOMAIN_ORDER.filter((d) => (grouped.get(d) || []).length > 0),
    [grouped],
  );

  const [activeDomain, setActiveDomain] = useState<ReorderDomain | null>(
    domainsWithItems[0] ?? null,
  );

  const [orderState, setOrderState] = useState<Map<ReorderDomain, string[]>>(() => {
    const init = new Map<ReorderDomain, string[]>();
    grouped.forEach((list, d) => init.set(d, list.map((p) => p.id)));
    return init;
  });
  const [dirtyDomains, setDirtyDomains] = useState<Set<ReorderDomain>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewProduct, setPreviewProduct] = useState<ReorderProduct | null>(null);

  useEffect(() => {
    const next = new Map<ReorderDomain, string[]>();
    grouped.forEach((list, d) => next.set(d, list.map((p) => p.id)));
    setOrderState(next);
    setDirtyDomains(new Set());
    if (!activeDomain || !domainsWithItems.includes(activeDomain)) {
      setActiveDomain(domainsWithItems[0] ?? null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grouped]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !activeDomain) return;
      setOrderState((prev) => {
        const cur = prev.get(activeDomain) || [];
        const oldIdx = cur.indexOf(String(active.id));
        const newIdx = cur.indexOf(String(over.id));
        if (oldIdx < 0 || newIdx < 0) return prev;
        const next = arrayMove(cur, oldIdx, newIdx);
        const out = new Map(prev);
        out.set(activeDomain, next);
        return out;
      });
      setDirtyDomains((prev) => {
        const next = new Set(prev);
        next.add(activeDomain);
        return next;
      });
    },
    [activeDomain],
  );

  const handleCancel = useCallback(() => {
    const next = new Map<ReorderDomain, string[]>();
    grouped.forEach((list, d) => next.set(d, list.map((p) => p.id)));
    setOrderState(next);
    setDirtyDomains(new Set());
    setError(null);
  }, [grouped]);

  const handleSave = useCallback(async () => {
    if (dirtyDomains.size === 0 || saving) return;
    setSaving(true);
    setError(null);
    const productsById = new Map(products.map((p) => [p.id, p]));
    const payload: {
      hardware?: Array<{ id: string; displayOrder: number }>;
      software?: Array<{ id: string; displayOrder: number }>;
      consulting?: Array<{ id: string; displayOrder: number }>;
    } = {};
    for (const domain of Array.from(dirtyDomains)) {
      const ids = orderState.get(domain) || [];
      ids.forEach((id, i) => {
        const product = productsById.get(id);
        if (!product) return;
        const entry = { id, displayOrder: i * 10 };
        const bucket =
          product.kind === 'software' ? 'software'
          : product.kind === 'consulting' ? 'consulting'
          : 'hardware';
        if (!payload[bucket]) payload[bucket] = [];
        payload[bucket]!.push(entry);
      });
    }
    try {
      await marketplaceService.saveCatalogueOrder(snapshotId, payload);
      setDirtyDomains(new Set());
      if (onSaved) onSaved();
    } catch (e: any) {
      setError(e?.message || 'Failed to save order');
    } finally {
      setSaving(false);
    }
  }, [dirtyDomains, orderState, products, saving, snapshotId, onSaved]);

  if (domainsWithItems.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-sm px-6 py-12">
          <p className="text-base font-medium text-slate-700 mb-1">No products in this catalogue yet</p>
          <p className="text-sm text-slate-500">
            Add products to <span className="font-medium">{catalogueName}</span> from the Products tab to start ordering.
          </p>
        </div>
      </div>
    );
  }

  const activeIds = activeDomain ? (orderState.get(activeDomain) || []) : [];
  const activeProducts = activeIds
    .map((id) => products.find((p) => p.id === id))
    .filter((p): p is ReorderProduct => Boolean(p));

  const dirty = dirtyDomains.size > 0;

  // Coerce a ReorderProduct into a ProductDraft for the preview modal.
  const previewDraft: ProductDraft | null = previewProduct
    ? {
        id: previewProduct.id,
        kind: previewProduct.kind,
        domain: previewProduct.domain as any,
        name: previewProduct.name,
        vendorOrBrand: previewProduct.vendorOrBrand,
        description: previewProduct.description,
        price: previewProduct.price,
        pricingType: previewProduct.pricingType,
        billingCycle: previewProduct.billingCycle,
        image: previewProduct.image,
        sku: previewProduct.sku,
        vendorPartNumber: previewProduct.vendorPartNumber,
        source: previewProduct.source,
        soh: previewProduct.soh,
        features: previewProduct.features,
        planInclusions: previewProduct.planInclusions,
        enrichmentAttributes: previewProduct.enrichmentAttributes,
        vendorPreset: previewProduct.vendorPreset,
        inclusionApps: previewProduct.inclusionApps,
      }
    : null;

  return (
    <div className="flex-1 flex flex-col bg-slate-50 overflow-hidden">
      {/* Tertiary nav — domain filter pills, customer-storefront chrome */}
      <div className="px-4 py-3 bg-white border-b border-slate-200 flex-shrink-0">
        <div className="flex items-center gap-2 overflow-x-auto">
          {domainsWithItems.map((d) => {
            const count = (grouped.get(d) || []).length;
            const isActive = activeDomain === d;
            const isDirty = dirtyDomains.has(d);
            return (
              <button
                key={d}
                type="button"
                onClick={() => setActiveDomain(d)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${
                  isActive
                    ? 'bg-orange-600 text-white shadow-sm'
                    : 'text-slate-700 hover:bg-slate-100 border border-transparent'
                }`}
              >
                <span>{DOMAIN_LABELS[d]}</span>
                <span className={`text-xs ${isActive ? 'text-white/80' : 'text-slate-400'}`}>
                  ({count})
                </span>
                {isDirty && (
                  <span className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-white' : 'bg-orange-500'}`} title="Unsaved changes" />
                )}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Drag a card to set the order customers see. Click a card to preview how it appears in their storefront.
        </p>
      </div>

      {/* Card grid — 1 / 2 / 3 columns matching the customer storefront.
          [scrollbar-gutter:stable] reserves the scrollbar lane regardless of
          whether content overflows, so switching between a long-tail domain
          (e.g. Telstra Handsets, 81 items) and a short one (Computers, 16)
          doesn't reflow the grid by ~17px.

          `w-full` on the grid is the second half of that fix: without it the
          grid's intrinsic width can fall back to the widest cell content
          (varies by domain — software icons vs hardware product photos), so
          the visible grid measured between 818px and 864px across tabs even
          with scrollbar-gutter set. Force the grid to fill its container
          and the cell widths become a function of parent width alone. */}
      <div className="flex-1 overflow-y-scroll p-6 [scrollbar-gutter:stable]">
        {activeDomain && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={activeIds} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr w-full max-w-7xl mx-auto">
                {activeProducts.map((p, i) => (
                  <SortableProductCard
                    key={p.id}
                    product={p}
                    index={i}
                    imageUrl={getCachedImageUrl ? getCachedImageUrl(p.image) : p.image}
                    onPreview={setPreviewProduct}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Sticky save footer */}
      {dirty && (
        <div className="border-t border-slate-200 bg-white px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="text-sm text-slate-600">
            {dirtyDomains.size === 1
              ? `Unsaved changes in ${DOMAIN_LABELS[Array.from(dirtyDomains)[0]]}`
              : `Unsaved changes in ${dirtyDomains.size} domains`}
            {error && (
              <span className="ml-3 text-rose-600">· {error}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleCancel} disabled={saving}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="mr-1.5 h-3.5 w-3.5" />
              )}
              {saving ? 'Saving…' : 'Save order'}
            </Button>
          </div>
        </div>
      )}

      <CustomerPreviewModal
        open={previewProduct != null}
        onClose={() => setPreviewProduct(null)}
        product={previewDraft}
        imageUrl={previewProduct && getCachedImageUrl ? getCachedImageUrl(previewProduct.image) : previewProduct?.image}
      />
    </div>
  );
}

export default CatalogueReorderView;
